import { useQuery, useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import { authService } from '../../services/authService';
import { apiJson, apiResponse, type ApiErrorBody } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import type {
  CommitteeFactsDto,
  MessagingProfileDto,
  MessagingProfileQueueResponse,
  UpdateMessagingProfileRequest,
  AdvanceProvisioningRequest,
  SaveCommitteeFactsRequest,
} from '@signalvote/shared';

const BASE = '/api/messaging/profile';

/**
 * Typed error for the messaging-profile mutations. Carries the backend's stable
 * error `code` (e.g. `ConcurrencyConflict`, `IllegalTransition`,
 * `UseCaseInfoIncomplete`) so callers can branch on it without parsing the message.
 */
export class MessagingProfileError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'MessagingProfileError';
    this.code = code;
  }
}

function profileError(fallback: string) {
  return (body: ApiErrorBody) => new MessagingProfileError(body.error || fallback, body.code);
}

/**
 * The selected campaign's messaging profile, or `null` when it has not been created
 * yet (the API returns 404). A null result is the "no profile yet" state the page
 * renders a Create button for — it is NOT an error.
 */
export function useMessagingProfile() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.messagingProfile.detail(),
    queryFn: async (): Promise<MessagingProfileDto | null> => {
      const response = await apiResponse(BASE);
      if (response.status === 404) return null;
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
        throw profileError('Failed to load messaging profile')(body);
      }
      return response.json() as Promise<MessagingProfileDto>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

/** What the selected campaign told us about its committee, prefilled where it hasn't, and where registration stands. */
export function useCommitteeFacts({ enabled = true, refetchOnMount }: { enabled?: boolean; refetchOnMount?: 'always' } = {}) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.messagingProfile.committee(),
    queryFn: () => getApiClient().messagingProfile.getCommitteeFacts(),
    enabled: enabled && !!selectedCampaign,
    refetchOnMount,
  });
}

/** Which form saved the committee facts: the facts themselves, or the signature box. */
export type CommitteeSaveDoor = 'facts' | 'signature';

/**
 * Saves the selected campaign's committee facts, with the attestation when the signer confirms.
 * The token is read from the cached facts when the save runs, so a caller that waits for one save
 * before starting the next never sends a stale one. Refusals are `ApiError`s whose body names the
 * `field`.
 */
export function useSaveCommitteeFacts(door: CommitteeSaveDoor) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.messagingProfile.committeeSave(door),
    mutationFn: (body: Omit<SaveCommitteeFactsRequest, 'expectedConcurrencyVersion'>): Promise<CommitteeFactsDto> =>
      getApiClient().messagingProfile.saveCommitteeFacts({
        ...body,
        expectedConcurrencyVersion:
          queryClient.getQueryData<CommitteeFactsDto>(queryKeys.messagingProfile.committee())?.concurrencyVersion ?? 0,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.messagingProfile.committee(), saved);
      queryClient.invalidateQueries({ queryKey: queryKeys.messagingProfile.detail() });
    },
  });
}

/**
 * The form behind this page's last successful committee save, or null when there was none, as after a
 * reload. The server keeps no record of why a signature came off; this is the web's own memory of it.
 */
export function useLastCommitteeSaveDoor(): CommitteeSaveDoor | null {
  const doors = useMutationState({
    filters: { mutationKey: queryKeys.messagingProfile.committeeSaves(), status: 'success' },
    select: (mutation) => mutation.options.mutationKey?.at(-1) as CommitteeSaveDoor,
  });
  return doors.at(-1) ?? null;
}

/** Every campaign's registration, waiting sends first in send order (the API sorts; do not re-sort). */
export function useMessagingProfiles() {
  return useQuery({
    queryKey: queryKeys.messagingProfiles.list(),
    queryFn: () =>
      apiJson<MessagingProfileQueueResponse>('/api/messaging/profiles', {
        error: profileError('Failed to load messaging profiles'),
      }),
    enabled: authService.isAuthenticated(),
  });
}

/** Creates the profile (NotStarted) for the selected campaign. */
export function useCreateMessagingProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (): Promise<MessagingProfileDto> =>
      apiJson<MessagingProfileDto>(BASE, {
        method: 'POST',
        error: profileError('Failed to create messaging profile'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.messagingProfile.all });
    },
  });
}

/** Records the Campaign Verify pass + captures the use-case fields (partial update). */
export function useUpdateMessagingProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateMessagingProfileRequest): Promise<MessagingProfileDto> =>
      apiJson<MessagingProfileDto>(BASE, {
        method: 'PATCH',
        json: data,
        error: profileError('Failed to update messaging profile'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.messagingProfile.all });
    },
  });
}

/** Advances the provisioning spine by one hand-driven edge. */
export function useAdvanceProvisioning() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AdvanceProvisioningRequest): Promise<MessagingProfileDto> =>
      apiJson<MessagingProfileDto>(`${BASE}/advance`, {
        method: 'POST',
        json: data,
        error: profileError('Failed to advance provisioning'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.messagingProfile.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.messagingProfiles.list() });
    },
  });
}
